import { CountryLocalisationConfig } from "../types/localized";

const DEFAULT_COUNTRY_KEY = "UAE";

type LocalisedIdDocument = {
  key: string;
  label: string;
};

type LocalisedAddressLabels = {
  stateLabel: string;
  cityLabel: string;
  postalCodeLabel: string;
};

type LocalisedMotorLabels = {
  vehicleValueLabel: string;
  vehicleValueSummaryLabel: string;
  vehicleValuePlaceholder: string;
  gccSpecificationLabel: string;
};

type LocalisationProfile = {
  homeCountryLabel: string;
  localeTag: string;
  dateFormat: string;
  idDocuments: LocalisedIdDocument[];
  vehicleDocuments: string[];
  motor: LocalisedMotorLabels;
  address: LocalisedAddressLabels;
  defaultNationality: string;
};

const COUNTRY_CONFIG_MAP: Record<string, CountryLocalisationConfig> = {
  UAE: {
    country: "UAE",
    aliases: ["AE", "ARE"],
    currencyCode: "AED",
    currencySymbol: "AED",
    mobileCountryCode: "971",
    mobileCountryIso: "AE",
    preferLocalCurrencyForDisplay: true,
    tax: {
      label: "VAT",
      rate: 0.05,
      percentage: 5,
    },
  },
  IND: {
    country: "IND",
    aliases: ["IN", "INDIA"],
    currencyCode: "INR",
    currencySymbol: "₹",
    mobileCountryCode: "91",
    mobileCountryIso: "IN",
    preferLocalCurrencyForDisplay: true,
    tax: {
      label: "GST",
      rate: 0.18,
      percentage: 18,
    },
  },
  SAU: {
    country: "SAU",
    aliases: ["SA", "SAUDI", "KSA"],
    currencyCode: "SAR",
    currencySymbol: "SAR",
    mobileCountryCode: "966",
    mobileCountryIso: "SA",
    preferLocalCurrencyForDisplay: true,
    tax: {
      label: "VAT",
      rate: 0.15,
      percentage: 15,
    },
  },
};

const LOCALISATION_PROFILE_MAP: Record<string, LocalisationProfile> = {
  UAE: {
    homeCountryLabel: "United Arab Emirates",
    localeTag: "en-AE",
    dateFormat: "DD MMM YYYY",
    idDocuments: [
      { key: "emiratesId", label: "Emirates ID" },
      { key: "passport", label: "Passport" },
    ],
    vehicleDocuments: ["Motor Policy", "Mulkiya Card", "Car Invoice"],
    motor: {
      vehicleValueLabel: "How much would you like to insure your car for?",
      vehicleValueSummaryLabel: "Car Value",
      vehicleValuePlaceholder: "Estimated car value in AED",
      gccSpecificationLabel: "Is your car GCC specification and unmodified?",
    },
    address: {
      stateLabel: "Emirate",
      cityLabel: "City",
      postalCodeLabel: "P.O. Box",
    },
    defaultNationality: "United Arab Emirates",
  },
  IND: {
    homeCountryLabel: "India",
    localeTag: "en-IN",
    dateFormat: "DD MMM YYYY",
    idDocuments: [
      { key: "aadhaar", label: "Aadhaar Number" },
      { key: "pan", label: "PAN Number" },
      { key: "passport", label: "Passport" },
    ],
    vehicleDocuments: ["Motor Policy", "Vehicle Registration Certificate", "Car Invoice"],
    motor: {
      vehicleValueLabel: "How much would you like to insure your car for?",
      vehicleValueSummaryLabel: "Car Value",
      vehicleValuePlaceholder: " Select your car value in INR",
      gccSpecificationLabel: "Is your car specification and unmodified?",
    },
    address: {
      stateLabel: "State",
      cityLabel: "City",
      postalCodeLabel: "PIN Code",
    },
    defaultNationality: "India",
  },
  SAU: {
    homeCountryLabel: "Saudi Arabia",
    localeTag: "en-SA",
    dateFormat: "DD MMM YYYY",
    idDocuments: [
      { key: "nationalId", label: "National ID / Iqama" },
      { key: "passport", label: "Passport" },
    ],
    vehicleDocuments: ["Motor Policy", "Vehicle Registration Certificate", "Car Invoice"],
    motor: {
      vehicleValueLabel: "How much would you like to insure your car for?",
      vehicleValueSummaryLabel: "Car Value",
      vehicleValuePlaceholder: "Estimated car value in SAR",
      gccSpecificationLabel: "Is your car GCC specification and unmodified?",
    },
    address: {
      stateLabel: "Province",
      cityLabel: "City",
      postalCodeLabel: "Postal Code",
    },
    defaultNationality: "Saudi Arabia",
  },
};

const normalizeCountryFromEnv = (value?: string): string => {
  const normalized = String(value || "")
    .trim()
    .toUpperCase();

  if (!normalized) {
    return DEFAULT_COUNTRY_KEY;
  }

  if (COUNTRY_CONFIG_MAP[normalized]) {
    return normalized;
  }

  const resolvedKey = Object.entries(COUNTRY_CONFIG_MAP).find(([, config]) =>
    [config.country, ...(config.aliases || [])]
      .map((entry) => entry.toUpperCase())
      .includes(normalized)
  )?.[0];

  return resolvedKey || DEFAULT_COUNTRY_KEY;
};

const resolvedCountry = normalizeCountryFromEnv(import.meta.env.VITE_APP_LOCALISATION_COUNTRY);
console.log("Resolved Country:", resolvedCountry);

export const LOCALISATION_CONFIG = COUNTRY_CONFIG_MAP[resolvedCountry];
export const LOCALISATION_PROFILE =
  LOCALISATION_PROFILE_MAP[resolvedCountry] || LOCALISATION_PROFILE_MAP[DEFAULT_COUNTRY_KEY];

export const getCurrentCountryKey = () => resolvedCountry;

export const getSupportedCountryKeys = () => Object.keys(COUNTRY_CONFIG_MAP);

export const normalizeCountryLabel = (value?: string): string => {
  if (!value) {
    return "";
  }

  const trimmedValue = String(value).trim();
  if (!trimmedValue) {
    return "";
  }

  const normalizedValue = trimmedValue.toUpperCase();

  const matchedCountryKey = Object.keys(COUNTRY_CONFIG_MAP).find((countryKey) => {
    const config = COUNTRY_CONFIG_MAP[countryKey];
    const profile = LOCALISATION_PROFILE_MAP[countryKey];

    return [countryKey, config.country, ...(config.aliases || []), profile.homeCountryLabel]
      .map((entry) =>
        String(entry || "")
          .trim()
          .toUpperCase()
      )
      .includes(normalizedValue);
  });

  if (matchedCountryKey) {
    return LOCALISATION_PROFILE_MAP[matchedCountryKey].homeCountryLabel;
  }

  return trimmedValue;
};

export const formatLocalisedIntegerInput = (value: unknown): string => {
  const digits = String(value ?? "").replace(/[^\d]/g, "");

  if (!digits) {
    return "";
  }

  const numericValue = Number(digits);
  if (!Number.isFinite(numericValue)) {
    return "";
  }

  const locale = LOCALISATION_CONFIG.currencyCode === "INR" ? "en-IN" : "en-US";

  return numericValue.toLocaleString(locale, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
};

export const getDisplayCurrencyCode = (inputCurrency?: string) => {
  const localCurrency = String(LOCALISATION_CONFIG.currencyCode || "").toUpperCase();

  if (LOCALISATION_CONFIG.preferLocalCurrencyForDisplay) {
    return localCurrency;
  }

  const normalizedInput = String(inputCurrency || "")
    .trim()
    .toUpperCase();

  return normalizedInput || localCurrency;
};

export const getLocaleTag = () => {
  return LOCALISATION_PROFILE.localeTag;
};

export const getCurrencySymbol = (inputCurrency?: string) => {
  const currencyCode = getDisplayCurrencyCode(inputCurrency || LOCALISATION_CONFIG.currencyCode);

  if (currencyCode === LOCALISATION_CONFIG.currencyCode && LOCALISATION_CONFIG.currencySymbol) {
    return LOCALISATION_CONFIG.currencySymbol;
  }

  const configSymbol = Object.values(COUNTRY_CONFIG_MAP).find(
    (config) => String(config.currencyCode || "").toUpperCase() === currencyCode
  )?.currencySymbol;

  if (configSymbol) {
    return configSymbol;
  }

  const fallbackSymbols: Record<string, string> = {
    USD: "$",
    EUR: "€",
    GBP: "£",
  };

  return fallbackSymbols[currencyCode] || currencyCode;
};

export const getTaxLabelWithRate = () =>
  `${LOCALISATION_CONFIG.tax.label} (${LOCALISATION_CONFIG.tax.percentage}%)`;

export const getExcludingTaxLabel = () => `Excl. ${LOCALISATION_CONFIG.tax.label}`;

export const getLocalisedDateFormat = () => LOCALISATION_PROFILE.dateFormat;

export const getLocalisedIdDocuments = (): LocalisedIdDocument[] =>
  LOCALISATION_PROFILE.idDocuments;

export const getPrimaryIdDocumentLabel = () => LOCALISATION_PROFILE.idDocuments[0]?.label || "ID";

export const getSecondaryIdDocumentLabel = () =>
  LOCALISATION_PROFILE.idDocuments[1]?.label || "Passport";

export const getLocalisedAddressLabels = (): LocalisedAddressLabels => LOCALISATION_PROFILE.address;

export const getDefaultNationality = () => LOCALISATION_PROFILE.defaultNationality;

export const getHomeCountryLabel = () => {
  return LOCALISATION_PROFILE.homeCountryLabel;
};
